import Vue from 'vue'
import VueRouter from 'vue-router'

import Login from '@/views/Login.vue'
import Register from '@/views/Register.vue'
import PhotoTagging from '@/views/PhotoTagging.vue'
import QualityControl from '@/views/QualityControl.vue'
import GroupTagging from '@/views/GroupTagging.vue'
import GroupQualityControl from '@/views/GroupQualityControl.vue'
import PasswordReset from '@/views/PasswordReset.vue'
import PasswordChange from '@/views/PasswordChange.vue'
import ManageTags from '@/views/ManageTags.vue'
import ManageUsers from '@/views/ManageUsers'
import store from '@/store/index.js'
import QueryPhotos from "@/views/QueryPhotos.vue";
import Status from "@/views/Status.vue";
import PhotoCounting from '@/views/PhotoCounting.vue'
import CountQualityControl from '@/views/CountQualityControl.vue'
import GroupCounting from '@/views/GroupCounting.vue'
import GroupCountQC from '@/views/GroupCountQC.vue'
import ManageCountItems from '@/views/ManageCountItems.vue'
import VerificationRuns from '@/views/VerificationRuns.vue'
import VerificationRunForm from '@/views/VerificationRunForm.vue'
import VerificationRun from '@/views/VerificationRun.vue'
import VerificationSnapshots from '@/views/VerificationSnapshots.vue'
import RereviewBatches from '@/views/RereviewBatches.vue'
import BoxTagging from '@/views/BoxTagging.vue'


const routes = [
    {path: '/login', name: 'login', component: Login},
    {path: '/register', name: 'register', component: Register},
    {path: '/tagging', name: 'tagging', component: PhotoTagging},
    {path: '/password-reset', name: 'password-reset', component: PasswordReset},
    {path: '/password-change/:token', name: 'password-change', component: PasswordChange},
    {path: '/quality-control', name: 'quality-control', component: QualityControl},
    {path: '/group-tagging', name: 'group-tagging', component: GroupTagging},
    {path: '/group-qc', name: 'group-qc', component: GroupQualityControl},
    {path: '/manage-tags', name: 'manage-tags', component: ManageTags},
    {path: '/manage-users', name: 'manage-users', component: ManageUsers},
    {path: '/query-photos', name: 'query-photos', component: QueryPhotos},
    {path: '/status', name: 'status', component: Status},
    {path: '/counting', name: 'counting', component: PhotoCounting},
    {path: '/count-qc', name: 'count-qc', component: CountQualityControl},
    {path: '/group-counting', name: 'group-counting', component: GroupCounting},
    {path: '/group-count-qc', name: 'group-count-qc', component: GroupCountQC},
    {path: '/manage-count-items', name: 'manage-count-items', component: ManageCountItems},
    {path: '/box-tagging', name: 'box-tagging', component: BoxTagging},
    // Box-QC reuses the same canvas in review mode (quality_control queue +
    // isQc writes + Approve/Flag). [IT-745]
    {path: '/box-qc', name: 'box-qc', component: BoxTagging, props: {reviewMode: true}},
    {path: '/verification', name: 'verification-runs', component: VerificationRuns},
    {path: '/verification/runs/new', name: 'verification-run-new', component: VerificationRunForm},
    {path: '/verification/runs/:id', name: 'verification-run', component: VerificationRun},
    {path: '/verification/snapshots', name: 'verification-snapshots', component: VerificationSnapshots},
    {path: '/verification/rereview-batches', name: 'verification-rereview-batches', component: RereviewBatches},
]

const verificationRouteNames = new Set([
    'verification-runs',
    'verification-run-new',
    'verification-run',
    'verification-snapshots',
    'verification-rereview-batches',
])

Vue.use(VueRouter)
const router = new VueRouter({
    scrollBehavior() {
        return {x: 0, y: 0}
    },
    mode: 'history',
    routes
})

const authExcludedRoutes = [
    //Include Routes that SHOULD NOT check for authorization here
    'login',
    'register',
    'password-reset',
    'password-change'

]

// Best landing page for the current user given their roles + capture mode.
// Used as the redirect target when a user hits a route they can't or shouldn't see.
function homeRouteForUser() {
    const roles = (store.state.auth.profile && store.state.auth.profile.user && store.state.auth.profile.user.roles) || []
    if (roles.includes('Tagger')) return store.getters.taggerHomeRoute
    if (roles.includes('Quality Control')) return store.getters.qcHomeRoute
    if (roles.includes('Sys Admin')) return store.getters.adminHomeRoute
    if (roles.includes('Auth Admin')) return '/manage-users'
    return '/login'
}

// Routes that belong to a capture mode other than the tenant's, and where to
// send the user instead. Keyed by route name, then by the capture mode the
// visitor is actually in — so a route lists every mode it does NOT belong to.
//
// Keyed by mode rather than a tag/count boolean because there are three modes
// now (tag, count, bbox); a ternary silently picked the wrong branch for any
// mode it didn't know about, letting a bbox tenant land on /tagging.
const captureModeRouteSwaps = {
    // Tag-mode routes.
    'tagging':         {count: 'taggerHomeRoute', bbox: 'taggerHomeRoute'},
    'group-tagging':   {count: 'taggerHomeRoute', bbox: 'taggerHomeRoute'},
    'quality-control': {count: 'qcHomeRoute',     bbox: 'qcHomeRoute'},
    'group-qc':        {count: 'qcHomeRoute',     bbox: 'qcHomeRoute'},
    'manage-tags':     {count: 'adminHomeRoute'},
    // Count-mode routes.
    'counting':           {tag: 'taggerHomeRoute', bbox: 'taggerHomeRoute'},
    'group-counting':     {tag: 'taggerHomeRoute', bbox: 'taggerHomeRoute'},
    'count-qc':           {tag: 'qcHomeRoute',     bbox: 'qcHomeRoute'},
    'group-count-qc':     {tag: 'qcHomeRoute',     bbox: 'qcHomeRoute'},
    'manage-count-items': {tag: 'adminHomeRoute',  bbox: 'adminHomeRoute'},
    // Bbox-mode route.
    'box-tagging':        {tag: 'taggerHomeRoute', count: 'taggerHomeRoute'},
}

router.beforeEach(async (to, from, next) => {

    if (store.state.auth.loggedIn && !store.state.settings.settingsLoaded) {
        // Mode-aware redirects below depend on captureMode/taggingMode being known.
        // On a hard refresh the store still has defaults, so wait for the first
        // settings load before deciding where this user should go.
        await store.dispatch('fetchSettings')
    }

    // No `path: '/'` route is registered above (the routes list starts at
    // '/login'). Without this, a logged-in user hitting the bare domain
    // gets a blank page — VueRouter has no match, no view renders. Send
    // them to their role/mode-appropriate home. Logged-out users continue
    // through to the /login redirect below.
    if (store.state.auth.loggedIn && (to.path === '/' || !to.name)) {
        next(homeRouteForUser())
        return
    }

    if (store.state.auth.loggedIn) {

        // Tagger role routes (photo and group tagging, counting)
        if (!store.state.auth.profile.user.roles.includes('Tagger') &&
            (to.name === 'tagging' || to.name === 'group-tagging' ||
             to.name === 'counting' || to.name === 'group-counting')) {
            next(homeRouteForUser())
            return
        }

        if (!store.state.auth.profile.user.roles.includes('Sys Admin') &&
            (to.name === 'manage-tags' || to.name === 'manage-count-items')) {
            next(homeRouteForUser())
            return
        }

        // QC role routes (photo and group QC, count QC)
        if (!store.state.auth.profile.user.roles.includes('Quality Control') &&
            (to.name === 'quality-control' || to.name === 'group-qc' ||
             to.name === 'count-qc' || to.name === 'group-count-qc')) {
            next(homeRouteForUser())
            return
        }

        if (!store.state.auth.profile.user.roles.includes('Auth Admin') && to.name === 'manage-users') {
            next(homeRouteForUser())
            return
        }

        if (!store.state.auth.profile.user.roles.includes('Quality Control') &&
            !store.state.auth.profile.user.roles.includes('Sys Admin') &&
            to.name === 'status') {
            next(homeRouteForUser())
            return
        }

        // Tag verification routes — require feature flag AND role.
        if (verificationRouteNames.has(to.name)) {
            const enabled = store.getters.isTagVerificationEnabled
            const roles = store.state.auth.profile.user.roles
            const allowed = enabled && (roles.includes('Tag Verifier') || roles.includes('Sys Admin'))
            if (!allowed) {
                next(homeRouteForUser())
                return
            }
        }

        // Mode-aware redirect: a user landing on a route belonging to the
        // wrong capture mode (e.g. /tagging on a count platform) gets sent
        // to the equivalent for their mode. Settings must be loaded.
        const swap = captureModeRouteSwaps[to.name]
        if (swap) {
            const target = swap[store.state.settings.captureMode]
            if (target) {
                const dest = store.getters[target]
                if (dest && dest !== to.path) {
                    next(dest)
                    return
                }
            }
        }
    }

    if (store.state.auth.loggedIn === false && !authExcludedRoutes.includes(to.name)) {
        next('/login')
    } else {
        next()
    }
})

export default router
